import { useCallback, useState } from 'react';

import {
	cancelSubscription,
	SubscriptionActionTypes,
	useBusinessLayer,
	useDTicketSubscription,
} from '@mopla/business-logic';
import { CancellationReasonEnum } from '@mopla/data-models';

import { usePassengerAppNavigate } from '../../../../hooks';
import { ECancellationCase, TSuccessModalVariant } from '../types';

import { useDTicketSubscriptionCancellationData } from './useDTicketSubscriptionCancellationData';

export const useDTicketSubscriptionCancel = () => {
	const { navigateToUserDTicket } = usePassengerAppNavigate();
	const bl = useBusinessLayer();
	const { subscription } = useDTicketSubscription();
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
	const [isCancelLoading, setIsCancelLoading] = useState(false);

	const {
		isLoading: isSubscriptionLoading,
		cancellationCase,
		dates,
		hasPendingCancel,
	} = useDTicketSubscriptionCancellationData();

	const successModalVariant: TSuccessModalVariant =
		cancellationCase === ECancellationCase.ActivePauseCancelOutTime
			? 'cancel_in_pause_out_time'
			: cancellationCase === ECancellationCase.ActivePause
			? 'cancel_in_pause'
			: cancellationCase === ECancellationCase.PreorderCancelInTime
			? 'preorder_cancel_in_time'
			: cancellationCase === ECancellationCase.PreorderCancelOutTime
			? 'preorder_cancel_out_time'
			: 'cancel';

	const handleCancel = useCallback(
		(reason: CancellationReasonEnum, otherReasonText?: string) => {
			if (!subscription?.id) return;

			setIsCancelLoading(true);

			bl.dispatch(
				cancelSubscription({
					subscriptionId: subscription.id,
					cancellationReason: reason,
					cancellationReasonDescription: otherReasonText,
				})
			);

			bl.watchActions({
				types: SubscriptionActionTypes.CancelSubscriptionResult,
				callback: (action) => {
					setIsCancelLoading(false);

					if (action.payload?.error) {
						console.error(
							'Error cancelling subscription:',
							action.payload.error
						);
					} else {
						setIsSuccessModalOpen(true);
					}
				},
			});
		},
		[subscription?.id, bl]
	);

	const closeSuccessModal = useCallback(() => {
		setIsSuccessModalOpen(false);
		navigateToUserDTicket();
	}, [navigateToUserDTicket]);

	return {
		isSubscriptionLoading,
		isCancelLoading,
		isSuccessModalOpen,
		successModalVariant,
		cancellationCase,
		dates,
		hasPendingCancel,
		handleCancel,
		closeSuccessModal,
	};
};
